import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import { Input } from "@tubo/ui/components/ui/input";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tubo/ui/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import { Search, Plus, Package, Trash2 } from "lucide-react";
import {
  useSemanticDataModelList,
} from "@tubo/api-models/hooks/semantic-data-model-api.hooks";
import { useSemanticNamespaceList } from "@tubo/api-models/hooks/semantic-namespace-api.hooks";
import type { SemanticDataModelListItemVO } from "@tubo/api-models/hooks/semantic-data-model-api.schemas";
import { ImportPackagePanel } from "@client/components/semantic-platform/data-models/import-package-panel";
import { DeleteModelDialog } from "@client/components/semantic-platform/data-models/delete-model-dialog";
import { NS_CONTEXT_ALL, NS_CONTEXT_GLOBAL, isGlobalNamespace, isPackNamespace } from "@tubo/semantic-concepts";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useForkSemanticDataModel, getSemanticDataModelListQueryKey } from "@tubo/api-models/hooks/semantic-data-model-api.hooks";
import { Copy, Lock } from "lucide-react";
import { ForkDialog } from "@client/components/semantic-platform/fork-dialog";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";

export const Route = createFileRoute("/_app/semantic-platform/data-models/")({
  // 领域上下文入 URL（缺省=全局；__all__=全部领域聚合浏览）
  validateSearch: (search: Record<string, unknown>): { ns?: string } => {
    return typeof search.ns === "string" && search.ns ? { ns: search.ns } : {};
  },
  component: DataModelListPage,
});

const PAGE_SIZE = 20;

function DataModelListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SemanticDataModelListItemVO | null>(null);

  // 领域上下文（切换器与列表写同一 URL 参数）；聚合浏览模式下提供领域筛选
  const { ns, nsFilter, setNs } = useNsContext();
  const showNsFilter = ns === NS_CONTEXT_ALL;

  const { data, isLoading } = useSemanticDataModelList(undefined, { query: {} });
  const [forkTarget, setForkTarget] = useState<SemanticDataModelListItemVO | null>(null);
  const { data: namespaces = [] } = useSemanticNamespaceList({ query: {} });
  const packNsIds = useMemo(
    () => new Set(namespaces.filter((ns) => isPackNamespace(ns.code)).map((ns) => ns.id)),
    [namespaces],
  );
  const userDomains = useMemo(
    () => namespaces
      .filter((ns) => !isPackNamespace(ns.code) && !isGlobalNamespace(ns.code))
      .map((ns) => ({ id: ns.id, name: ns.name })),
    [namespaces],
  );
  const forkMutation = useForkSemanticDataModel({
    mutation: {
      onSuccess: (detail) => {
        queryClient.invalidateQueries({ queryKey: getSemanticDataModelListQueryKey() });
        toast.success(`已复制为「${detail?.name ?? "数据模型"}」（Draft）`);
        setForkTarget(null);
      },
      onError: () => toast.error("复制失败"),
    },
  });
  const nsNameById = useMemo(
    () => new Map(namespaces.map((ns) => [ns.id, ns.name])),
    [namespaces],
  );
  const globalNsIds = useMemo(
    () => new Set(namespaces.filter((ns) => isGlobalNamespace(ns.code)).map((ns) => ns.id)),
    [namespaces],
  );
  const nsFilterOptions = useMemo(
    () => [
      { value: NS_CONTEXT_ALL, label: "全部领域" },
      { value: NS_CONTEXT_GLOBAL, label: "全局" },
      ...namespaces.filter((ns) => !isGlobalNamespace(ns.code)).map((ns) => ({ value: ns.id, label: ns.name })),
    ],
    [namespaces],
  );

  const filtered = useMemo(() => {
    const items: SemanticDataModelListItemVO[] = data ?? [];
    const matchesNs = (i: SemanticDataModelListItemVO) =>
      nsFilter === NS_CONTEXT_ALL ||
      (nsFilter === NS_CONTEXT_GLOBAL
        ? !i.namespaceId || globalNsIds.has(i.namespaceId)
        : i.namespaceId === nsFilter);
    if (!keyword.trim() && nsFilter === NS_CONTEXT_ALL) return items;
    const k = keyword.toLowerCase();
    return items.filter((i) => matchesNs(i) && (!keyword.trim() || i.name?.toLowerCase().includes(k) || i.code?.toLowerCase().includes(k)));
  }, [data, globalNsIds, keyword, nsFilter]);

  const total = filtered.length;
  const pagedData = filtered.slice((page - 1) * pageSize, page * pageSize);

  const columns: DataTableColumn<SemanticDataModelListItemVO>[] = [
    { id: "code", header: "编码", cell: (r) => <span className="font-mono text-sm">{r.code}</span> },
    { id: "name", header: "名称", cell: (r) => <span className="font-medium">{r.name}</span> },
    { id: "currentVersion", header: "版本", cell: (r) => r.currentVersion ?? "—" },
    {
      id: "namespace",
      header: "所属领域",
      cell: (r) => {
        const locked = !!r.namespaceId && packNsIds.has(r.namespaceId);
        return r.namespaceId ? (
          <Badge variant="outline">
            {r.namespaceName ?? nsNameById.get(r.namespaceId) ?? r.namespaceId}
            {locked && <Lock className="ml-1 inline size-3 text-muted-foreground" />}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">全局</span>
        );
      },
    },
    { id: "businessCategory", header: "业务分类", cell: (r) => r.businessCategory ?? "—" },
    {
      id: "actions",
      header: "操作",
      cell: (r) => {
        const locked = !!r.namespaceId && packNsIds.has(r.namespaceId);
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {locked && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="复制到我的领域"
                title="行业包资产只读，点击复制到我的领域后编辑"
                onClick={() => setForkTarget(r)}
              >
                <Copy className="size-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="删除"
              title={locked ? "行业包资产只读" : "删除(进回收站,可恢复;有派生或引用时不可删)"}
              className="text-destructive"
              disabled={locked}
              onClick={() => setDeleteTarget(r)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        );
      },
    },
  ];

  const headerTitle = useMemo(
    () => <span className="text-sm font-semibold">语义数据模型</span>,
    [],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {showNsFilter && (
          <Select items={nsFilterOptions} value={nsFilter} onValueChange={(value) => { setNs(value ?? NS_CONTEXT_ALL); setPage(1); }}>
            <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{nsFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="w-56 pl-8"
            placeholder="搜索编码或名称"
            value={keyword}
            onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
          <Package className="mr-1 size-4" /> 导入模型包
        </Button>
        <Button size="sm" onClick={() => navigate({ to: "/semantic-platform/data-models/edit/$id", params: { id: "new" }, search: ns ? { ns } : {} })}>
          <Plus className="mr-1 size-4" /> 新建
        </Button>
      </div>
    ),
    [keyword, navigate, ns, nsFilter, nsFilterOptions, showNsFilter, setNs],
  );
  useSecondaryNavActions(headerActions);

  return (
    <div className="h-full min-h-0 min-w-0">
      <DataTable
        data={pagedData}
        columns={columns}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={total}
        onPageChange={setPage}
        onRowClick={(row) => navigate({ to: "/semantic-platform/data-models/$id", params: { id: row.id }, search: ns ? { ns } : {} })}
      />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>模型包导入</DialogTitle>
            <DialogDescription>
              粘贴 ModelPackageManifest JSON 导入为草稿模型(6C-4 六步任务，完成后自动确认落地)。
            </DialogDescription>
          </DialogHeader>
          <ImportPackagePanel onImportSuccess={() => setImportOpen(false)} />
        </DialogContent>
      </Dialog>

      <DeleteModelDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        id={deleteTarget?.id ?? ""}
        name={deleteTarget?.name ?? ""}
      />

      <ForkDialog
        open={forkTarget !== null}
        onOpenChange={(v) => !v && setForkTarget(null)}
        sourceLabel={forkTarget?.name}
        domains={userDomains}
        onFork={async (targetNamespaceId) => {
          if (!forkTarget) return;
          await forkMutation.mutateAsync({ id: forkTarget.id, data: { targetNamespaceId } });
          return forkTarget.id;
        }}
      />
    </div>
  );
}
